import { isGranted } from '@utils/isGranted'
import React, { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { hasImpersonationAdminExitPending } from '@/auth/keycloakExchangeSession'
import { FullScreenLoader } from '@/components/atoms/Loader/FullScreenLoader'
import { Unauthorized403 } from '@/components/molecules/Error/Unauthorized403'
import { Button } from '@/components/ui/button'
import {
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog'
import { useSwitchContextState } from '@/contexts/SwitchContextContext'
import { useContext } from '@/hooks/useContext'
import { useSwitchContext } from '@/hooks/useSwitchContext'
import type { RequireAuthGrant } from '@/routes/_hooks/requireAuthGrants'
import { NetworksRoutes, StaticPagesRoutes } from '@/routes/_hooks/routes.enum'
import { Navigate, useLocation, useNavigate, useParams } from '@/routing'
import { isContextSwitchIntentActive } from '@/utils/contextSwitchIntent'
import { getContextSwitchTargetPath } from '@/utils/contextSwitchNavigation'

import { useAuth } from './useAuth'

/** In-memory memo for identical grant checks (same user, attribute, resource). */
const recentGrantDecisions = new Map<string, boolean>()

function grantLookupKey(
  userId: string,
  attribute: string,
  resource: string
): string {
  return `${userId}::${attribute}::${resource}`
}

export const RequireAuth = ({
  children,
  permission,
  grant,
}: {
  children: ReactElement
  permission?: string
  grant?: RequireAuthGrant
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const params = useParams()
  const auth = useAuth()
  const { isUser, contextIri } = useContext()
  const { switchContext } = useSwitchContext()
  const { isSwitching } = useSwitchContextState()
  const [grantDecision, setGrantDecision] = useState<boolean | undefined>(
    undefined
  )
  const resourceIdentifier = grant?.resourceParamKey
    ? params[grant.resourceParamKey]
    : params.slug

  useEffect(() => {
    if (!grant) return
    if (auth.loading) return
    // Missing resource in URL (e.g. /my/communities//dashboard) -> real error
    if (!resourceIdentifier) {
      setGrantDecision(false)
      return
    }
    // No user yet (auth still resolving) -> keep loader
    const userId = auth.user?.id
    if (!userId) {
      setGrantDecision(undefined)
      return
    }
    const resource = grant.buildResource(resourceIdentifier)
    const lookupKey = grantLookupKey(userId, grant.attribute, resource)
    const memo = recentGrantDecisions.get(lookupKey)
    if (memo !== undefined) {
      setGrantDecision(memo)
      return
    }

    let isActive = true
    setGrantDecision(undefined)
    isGranted({
      attribute: grant.attribute,
      resource,
      accessToken: auth.token ?? undefined,
    }).then((decision) => {
      if (!isActive) {
        return
      }
      recentGrantDecisions.set(lookupKey, decision)
      setGrantDecision(decision)
    })
    return () => {
      isActive = false
    }
  }, [auth.loading, auth.user?.id, grant, resourceIdentifier])

  // Wait for OIDC bootstrap + Apollo `currentUser` before any auth decision (avoids flash /login).
  if (auth.loading) {
    return <FullScreenLoader />
  }

  // If we have a token but no user yet, we are still loading the user profile.
  if (auth.token && !auth.user) {
    return <FullScreenLoader />
  }

  if (!auth.user) {
    // Pass the intended destination as `?from=` (Next has no navigation state).
    const from = encodeURIComponent(`${location.pathname}${location.search}`)
    return <Navigate to={`/login?from=${from}`} replace />
  }

  if (auth.user && permission && !auth.user.roles?.includes(permission)) {
    if (hasImpersonationAdminExitPending()) {
      return <FullScreenLoader />
    }
    return <Unauthorized403 />
  }
  if (grant) {
    if (isSwitching) {
      return <FullScreenLoader />
    }
    if (grantDecision === undefined) {
      return <FullScreenLoader />
    }
    if (grantDecision === false) {
      return <Unauthorized403 />
    }
  }

  const isPersonalRoute = location.pathname.startsWith('/my/')
  const isNetworkManagementRoute = location.pathname.startsWith('/n/')
  const networkSlugFromRoute = params.networkSlug ?? params.slug
  const shouldPromptForPersonalContextSwitch = isPersonalRoute && !isUser
  const shouldPromptForNetworkContextSwitch =
    isNetworkManagementRoute && isUser && Boolean(networkSlugFromRoute)
  const shouldSuppressPromptDuringIntent =
    (shouldPromptForPersonalContextSwitch ||
      shouldPromptForNetworkContextSwitch) &&
    isContextSwitchIntentActive()

  if (shouldSuppressPromptDuringIntent) {
    const targetPath = getContextSwitchTargetPath({
      currentPathname: location.pathname,
      targetContextIri: contextIri,
    })
    const targetUrl = `${targetPath}${location.search}`
    const currentUrl = `${location.pathname}${location.search}`
    if (targetUrl !== currentUrl) {
      return <Navigate to={targetUrl} replace />
    }
  }

  if (shouldPromptForPersonalContextSwitch) {
    const handleSwitchToPersonalContext = async () => {
      await switchContext(null, auth.user?.firstname ?? null)
      navigate(`${location.pathname}${location.search}`, { replace: true })
    }

    return (
      <DialogRoot
        open={true}
        closeOnEscape={false}
        closeOnInteractOutside={false}
      >
        <DialogContent maxW="520px">
          <DialogHeader>
            <DialogTitle>{t('context.personalRouteDialog.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <DialogDescription>
              {t('context.personalRouteDialog.description')}
            </DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                navigate(StaticPagesRoutes.Home, { replace: true })
              }
            >
              {t('global:cancel')}
            </Button>
            <Button
              colorPalette="brand"
              loading={isSwitching}
              onClick={handleSwitchToPersonalContext}
            >
              {t('context.personalRouteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    )
  }

  if (shouldPromptForNetworkContextSwitch) {
    const handleSwitchToNetworkContext = async () => {
      const networkIri = `/networks/${networkSlugFromRoute}`
      await switchContext(networkIri, null)
      navigate(`${location.pathname}${location.search}`, { replace: true })
    }

    return (
      <DialogRoot
        open={true}
        closeOnEscape={false}
        closeOnInteractOutside={false}
      >
        <DialogContent maxW="520px">
          <DialogHeader>
            <DialogTitle>{t('context.networkRouteDialog.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <DialogDescription>
              {t('context.networkRouteDialog.description')}
            </DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                navigate(NetworksRoutes.MyNetworks, { replace: true })
              }
            >
              {t('global:cancel')}
            </Button>
            <Button
              colorPalette="brand"
              loading={isSwitching}
              onClick={handleSwitchToNetworkContext}
            >
              {t('context.networkRouteDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    )
  }

  return children
}
